<template>
	<div class="chat-log h-100 d-flex flex-column overflow-hidden">
		<horizontal-scroller class="chat-alerts-container">
			<TransitionGroup class="chat-alerts" name="wedge" tag="div">
				<div :class="[ 'donation', ...alert.classList ]" v-for="alert in alerts" :key="alert.id" @click="selectedMessage = alert">
					<b-avatar size="1.5em" :src="alert.avatarUrl||alert.serviceIconUrl" badge-variant="none">
						<template #badge><b-avatar size="1.5em" :src="alert.serviceIconUrl" v-if="alert.avatarUrl"/></template>
					</b-avatar>
					<span class="donation-amount" v-if="alert.isGift">{{alert.giftAmount}}</span>
					<span class="short" v-if="alert.shortEvent">{{alert.shortEvent}}</span>
					<div class="progress-bar" :style="`animation-duration: ${alert.animationDuration}s`"></div>
				</div>
			</TransitionGroup>
		</horizontal-scroller>
		<div class="d-flex h-100 flex-fill justify-content-center align-items-center" v-if="loading>0">
			<b-icon icon="circle-fill" animation="throb" font-scale="2"></b-icon>
		</div>
		<div v-show="loading <= 0" ref="chatLog" class="flex-fill overflow-auto dashboardChatLog" @scroll="chatLogScrolled($event)" />
		<div ref="scrollToBottom" class="chatLogScrollToBottom" v-show="historyMode"><b-button @click="bottomOut()"><b-icon icon="arrow-down"></b-icon></b-button></div>
		<div class="m-2">
			<b-alert v-model="showMessageError" :show="5" variant="danger" dismissible>{{messageError}}</b-alert>
			<b-input-group>
				<template #prepend>
					<b-dropdown dropup variant="secondary">
						<template #button-content>
							<b-avatar size="1.5rem" :src="channel?channel.avatar_url:logo"></b-avatar>
						</template>
						<b-dropdown-item @click="channel = null">
							<b-avatar size="2rem" class="mr-1" :src="logo"></b-avatar>
							All channels
						</b-dropdown-item>
						<b-dropdown-item v-for="c, i in channels" :key="i" @click="channel = c">
							<b-avatar size="2rem" :src="c.avatar_url" class="mr-1" badge-variant="dark"><template #badge><img :src="c.service_icon_url" style="width: 1.3em"></template></b-avatar>
							{{c.title}}
						</b-dropdown-item>
					</b-dropdown>
				</template>
				<b-input v-model="message" placeholder="Send message..." :readonly="messageLoading>0||!channel&&channels?.length==0" autocomplete="off" @keyup.enter.prevent.stop="message && sendMessage()"></b-input>
				<b-input-group-append>
					<b-button size="sm" variant="success" :disabled="!message || messageLoading > 0" @click="sendMessage()">
						<svg fill="#000000" height="24px" width="24px" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 495 495">
							<g>
								<path d="M164.711,456.687c0,2.966,1.647,5.686,4.266,7.072c2.617,1.385,5.799,1.207,8.245-0.468l55.09-37.616 l-67.6-32.22V456.687z"/>
								<path d="M492.431,32.443c-1.513-1.395-3.466-2.125-5.44-2.125c-1.19,0-2.377,0.264-3.5,0.816L7.905,264.422 c-4.861,2.389-7.937,7.353-7.904,12.783c0.033,5.423,3.161,10.353,8.057,12.689l125.342,59.724l250.62-205.99L164.455,364.414 l156.145,74.4c1.918,0.919,4.012,1.376,6.084,1.376c1.768,0,3.519-0.322,5.186-0.977c3.637-1.438,6.527-4.318,7.97-7.956 L494.436,41.257C495.66,38.188,494.862,34.679,492.431,32.443z"/>
							</g>
						</svg>
					</b-button>
				</b-input-group-append>
			</b-input-group>
		</div>
		<message-details-overlay
			:message="selectedMessage"
			:user="user"
			:serviceIcons="serviceIcons"
			@close="selectedMessage = null"
			@select="highlightMessage($event)"
			@replay="replayAlert($event)"
		/>
	</div>
</template>

<script>
import FetchPoller from '@/FetchPoller';
import lj3 from '@/assets/lj3.png';
import util from '@/util';
import giftColorThresholds from '@/GiftThresholds';
import HorizontalScroller from '@/components/HorizontalScroller.vue';
import MessageDetailsOverlay from '@/components/MessageDetailsOverlay.vue';

const styleId = 'chat-log-gift-color-thresholds-style';
let style = document.getElementById(styleId);
if(!style) {
	style = document.createElement('style');
	style.type = 'text/css';
	style.id = styleId;
	document.head.appendChild(style);
}

style.innerHTML = giftColorThresholds.map(t => `
	.chat-log .gift-${t.value},
	.chat-log .gift-${t.value} > .card-header,
	.chat-log > .chat-alerts-container .chat-alerts > .donation.gift.gift-${t.value},
	.chat-log > .dashboardChatLog > .message.gift.gift-${t.value}:not(.highlighted) {
		color: ${util.getContrastingTextColor(t.color)};
		background-color: ${t.color};
	}
	.chat-log .gift-${t.value} > .card-body,
	.chat-log > .chat-alerts-container .chat-alerts > .donation.gift.gift-${t.value}:hover,
	.chat-log > .dashboardChatLog > .message.gift.gift-${t.value}:not(.highlighted):hover {
		background-color: ${t.secondaryColor||t.color};
	}
`).join('');

export default {
	props: {
		serviceIcons: Object,
	},
	data() { return {
		loading: 0,
		log: [],
		historyMode: false,
		user: null,
		message: '',
		showMessageError: false,
		messageError: null,
		messageLoading: 0,
		channel: null,
		channels: null,
		logo: lj3,
		selectedMessage: null,
		alerts: [],
	}},
	components: {
		HorizontalScroller,
		MessageDetailsOverlay,
	},
	created() {
		this.removedOffset = 0;
		this.scrollPos = 0;
		this.prevHeight = 0;
	},
	async mounted() {
		try {
			this.loading++;
			this.$refs.chatLog.innerHTML = '';
			this.prevHeight = this.$refs.chatLog.clientHeight;
			this.prevScrollHeight = this.$refs.chatLog.scrollHeight;
			this.startScrollAnimation();
			this.channelPoller = new FetchPoller(async signal => {
				try {
					this.error = null;
					await this.loadChannels(signal);
				} catch(e) {
					if(!(e instanceof DOMException) || !(e.code == 20 || e.name == 'AbortError')) {
						this.error = e;
						console.error('Polling channels', e, e instanceof DOMException, e.code, e.name);
					}
				}
			}, 1 * 60 * 1000);
			this.user = await this.$api.get('userData');
			this.$socket.subscribe(this.user.widget_hash, {
				chat: msg => { if(!this.historyMode) this.appendMessages([msg]) },
				channelInfoUpdate: () => this.channelPoller.start(),
				channelDeleted: () => this.channelPoller.start(),
				channelCreated: () => this.channelPoller.start(),
				channelStatusChanged: () => this.channelPoller.start(),
				restreamStatus: () => this.channelPoller.start(),
				userWidgetAlert: msg => {
					if(msg.isReplay) return; // don't append replays
					const prepared = this.prepareAlert(msg.message);
					this.alerts = [
						prepared,
						...this.alerts,
					];
				},
				messageHighlighted: msg => {
					const log = this.$refs.chatLog;
					if(log) for(const h of Array.from(log.getElementsByClassName('highlighted'))) h.classList.remove('highlighted');
					this.user.highlighted_message = msg;
					document.getElementById(msg.id)?.classList.add('highlighted');
				},
				messageUnhighlighted: () => {
					const log = this.$refs.chatLog;
					if(log) for(const h of Array.from(log.getElementsByClassName('highlighted'))) h.classList.remove('highlighted');
					this.user.highlighted_message = null;
				},
			});

			const results = await this.$api.get(`userGiftTypes`);
			this.loadGiftTypes(results.results);
			await this.channelPoller.start(true);
			const alertResults = await this.$api.get('alerts');
			alertResults.reverse();
			const alerts = [];
			for(let r of alertResults) {
				const prepared = this.prepareAlert(r.message, r.sent_at);
				if(!prepared) continue;
				alerts.push(prepared);
			}
			this.alerts = alerts;
			await this.bottomOut();
		} catch(e) {
			console.error(e)
		} finally {
			this.loading--;
		}
	},
	beforeDestroy() {
		this.stopScrollAnimation();
		this.channelPoller.stop();
	},
	methods: {
		async loadChannels(signal) {
			this.channels = await this.$api.get('channels/chat', null, signal);
		},
		loadGiftTypes(giftTypes) {
			this.giftTypes = {};
			for(let giftType of giftTypes) {
				if(!(giftType.service_id in this.giftTypes))
					this.giftTypes[giftType.service_id] = {};
				this.giftTypes[giftType.service_id][giftType.gift_type] = giftType;
			}
		},
		startScrollAnimation() {
			this.animationRequestId = window.requestAnimationFrame(this.animationFrame.bind(this));
		},
		stopScrollAnimation() {
			if(!this.animationRequestId) return
			window.cancelAnimationFrame(this.animationRequestId);
			delete this.animationRequestId;
		},
		animationFrame() {
			if(!this.animationRequestId) return;
			this.animationRequestId = window.requestAnimationFrame(this.animationFrame.bind(this));
			if(!this.$refs.chatLog) return;
			if(this.historyMode) return;
			let scrollTopMax = this.$refs.chatLog.scrollHeight - this.$refs.chatLog.offsetHeight;
			let scrollDiff = scrollTopMax - this.$refs.chatLog.scrollTop;
			if(scrollDiff > 400) {
				this.$refs.chatLog.scrollTop += scrollDiff;
			} else {
				let scrollMove = Math.round(scrollDiff / 5) + 1;
				this.$refs.chatLog.scrollTop += scrollMove;
			}
		},
		chatLogScrolled(event) {
			const scrollBottom = event.target.scrollHeight - event.target.scrollTop - event.target.clientHeight;
			if(this.historyMode) {
				if(event.target.scrollHeight <= event.target.clientHeight) {
					this.historyMode = false;
				} else {
					if(scrollBottom + this.removedOffset < 1) {
						this.$api.get('chatLog', {
							limit: 100,
							afterId: this.$refs.chatLog.childNodes[this.$refs.chatLog.childNodes.length - 1].dataset.id
						}).then(log => {
							this.appendMessages(log);
							if(log.length < 100)
								this.historyMode = false;
						});
					} else if(event.target.scrollTop + this.removedOffset < 1) {
						this.$api.get('chatLog', {
							limit: 100,
							beforeId: this.$refs.chatLog.childNodes[0].dataset.id
						}).then(log => {
							const prevScrollHeight = event.target.scrollHeight;
							this.prependMessages(log);
							event.target.scrollTop = event.target.scrollHeight + this.removedOffset - prevScrollHeight;
						});
					}
				}
			} else {
				const scrolledPx = event.target.scrollTop - this.scrollPos;
				const resizedPx = event.target.clientHeight - this.prevHeight;
				const innerPx = event.target.scrollHeight - this.prevScrollHeight;
				if(innerPx == 0 && resizedPx == 0 && this.removedOffset == 0 && scrolledPx < 0)
					this.historyMode = true;
			}
			this.removedOffset = 0;
			this.scrollPos = event.target.scrollTop;
			this.prevHeight = this.$refs.chatLog.clientHeight;
			this.prevScrollHeight = this.$refs.chatLog.scrollHeight;
		},
		async bottomOut() {
			try {
				this.error = null;
				this.historyMode = false;
				// this.$refs.chatLog.scrollTop = this.$refs.chatLog.scrollTopMax || (this.$refs.chatLog.scrollHeight - this.$refs.chatLog.clientHeight);
				const log = await this.$api.get('chatLog', { limit: 200 });
				this.$refs.chatLog.innerHTML = '';
				this.appendMessages(log.reverse(), true);
				this.historyMode = false;
				this.$refs.chatLog.scrollTop = this.$refs.chatLog.scrollTopMax || (this.$refs.chatLog.scrollHeight - this.$refs.chatLog.clientHeight);
				await util.awaitMediaLoadedRecursive(this.$refs.chatLog);
				this.historyMode = false;
				this.$refs.chatLog.scrollTop = this.$refs.chatLog.scrollTopMax || (this.$refs.chatLog.scrollHeight - this.$refs.chatLog.clientHeight);
			} catch(e) {
				console.error(e);
				this.error = e;
			}
		},
		appendMessages(msgs, noRemove) {
			if(!this.$refs.chatLog) return;
			const log = this.$refs.chatLog;
			// append new elements at the bottom
			const fragment = document.createDocumentFragment();
			for(let msg of msgs) {
				try {
					fragment.appendChild(this.createMessageElem(msg));
				} catch(e) {
					console.error(e);
				}
			}
			log.appendChild(fragment);
			if(noRemove) return;
			// remove old elements from the top
			const last = log.childNodes[log.childNodes.length - 200];
			if(last)
				this.removedOffset = last.offsetTop + last.offsetHeight - log.firstChild.offsetTop;
			// create a ghost fragment which gets garbage collected
			const fragment2 = document.createDocumentFragment();
			while(log.childNodes.length > 200)
				fragment2.appendChild(log.firstChild);
		},
		prependMessages(msgs) {
			if(!this.$refs.chatLog) return;
			const log = this.$refs.chatLog;
			// prepend new messages at top
			const fragment = document.createDocumentFragment();
			for(let i = msgs.length - 1; i >= 0; i--) {
				try {
					fragment.appendChild(this.createMessageElem(msgs[i]));
				} catch(e) {
					console.error(e);
				}
			}
			log.prepend(fragment);
			// remove old messages from bottom
			this.removedOffset = log.lastChild.offsetTop + log.lastChild.offsetHeight - log.childNodes[200].offsetTop;
			const fragment2 = document.createDocumentFragment();
			while(log.childNodes.length > 200)
				fragment2.appendChild(log.lastChild);
		},
		createMessageElem(msg) {
			return util.createChatLogMessage(msg, {
				serviceIcons: this.serviceIcons,
				user: this.user,
				giftTypes: this.giftTypes,
				channels: this.channels,
				giftColorThresholds,
			}, {
				onclick: prepared => { this.selectedMessage = prepared; },
				onmenuclick: prepared => this.highlightMessage(prepared),
			});
		},

		highlightMessage(msg) {
			const target = document.getElementById(msg.id);
			target?.classList.add('loading');
			const is_current = String(msg.serviceId) == String(this.user.highlighted_message?.serviceId) && msg.token == this.user.highlighted_message?.token;
			this.$api.post('selectMessage', is_current ? null : msg).then(() => {
				const highlighted = this.$refs.chatLog.getElementsByClassName('highlighted');
				for(let h of highlighted)
					h.classList.remove('highlighted');
				if(is_current) {
					this.user.highlighted_message = null;
				} else {
					target?.classList.add('highlighted');
					this.user.highlighted_message = msg;
				}
			}).catch(err => {
				this.$bvToast.toast('Could not select message: '+(err.error||err.toString()), {
					title: 'Error',
					variant: 'danger',
					solid: true,
				});
			}).finally(() => {
				target?.classList.remove('loading');
			});
			this.$broadcastChannel?.postMessage({
				type: 'highlightedMessage',
				data: is_current ? null : JSON.parse(JSON.stringify(msg)),
			});
		},

		async sendMessage() {
			try {
				const message = this.message;
				this.message = '';
				this.showMessageError = false;
				this.messageError = null;
				this.messageLoading++;
				if(this.channel) {
					await this.$api.post('message', {
						serviceId: this.channel.service_id,
						channelToken: this.channel.token,
						message
					});
				} else {
					await Promise.all(this.channels.map(channel => this.$api.post('message', {
						serviceId: channel.service_id,
						channelToken: channel.token,
						message
					})));
				}
			} catch(e) {
				console.error(e);
				this.showMessageError = true;
				this.messageError = e.message;
			} finally {
				this.messageLoading--;
			}
		},
		prepareAlert(msg, sent_at) {
			const prepared = util.prepareMessage(msg, {
				serviceIcon: true, serviceName: true, serviceIconUrl: true,
				displayNameElem: true,
				classList: true,
				spansDom: true,
				giftElem: true, giftColorThreshold: true,
				eventString: true, shortEvent: true,
				canReply: true,
				avatarIcon: true, avatar: true,
				giftAmount: true,
				isGift: true, isAlert: true,
			}, {
				serviceIcons: this.serviceIcons,
				giftColorThresholds,
				user: this.user,
				giftTypes: this.giftTypes,
				channels: this.channels,
			});

			prepared.messages = msg.messages?.map(m => m.message);

			const now_ms = new Date().getTime();
			const sent_at_ms = sent_at ? new Date(sent_at).getTime() : now_ms;
			const duration_sec = Math.max(120, prepared.giftColorThreshold?.duration || 0);
			const expires_at_ms = sent_at_ms + duration_sec * 1000;
			if(expires_at_ms < now_ms) return;
			setTimeout(() => {
				const idx = this.alerts.findIndex(a => a.id == prepared.id);
				if(idx < 0) return;
				this.alerts.splice(idx, 1);
			}, expires_at_ms - now_ms);
			prepared.animationDuration = Math.floor((expires_at_ms - now_ms) / 1000);

			return prepared;
		},
		replayAlert(msg) {
			this.$api.post('alerts/replay', { serviceId: msg.serviceId, token: msg.token });
		},
	}
}
</script>

<style lang="scss">
@import '../plugins/variable.scss';

.chat-log {
	.sticker, .image { max-width: 80px; max-height: 80px; }
	.emote { max-width: 32px; max-height: 32px; }

	> .chat-alerts-container {
		.chat-alerts {
			display: flex;
			animation: scroll-left linear infinite;
			flex-direction: row;

			> .donation {
				cursor: pointer;
				background: $gray-800;
				color: $vaporwave-white;
				border-radius: .4em;
				padding: .3em;
				margin: .2em;
				display: flex;
				gap: .4em;
				flex-direction: row;
				align-items: center;
				font-weight: bold;
				white-space: nowrap;
				position: relative;

				&.gift {
					background: $gold;
				}

				.progress-bar {
					position: absolute;
					bottom: 0;
					left: 0;
					height: 4px;
					background: limegreen;
					width: 100%;
					animation: progressDrain 10s linear forwards;
				}

				/* Keyframes */
				@keyframes progressDrain {
					from { width: 100%; }
					to { width: 0%; }
				}

				@keyframes fadeOut {
					to { opacity: 0; transform: scale(0.9); }
				}
			}
		}
	}
	> .dashboardChatLog {
		word-break: break-word;
		transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

		> .message {
			cursor: pointer;
			padding: 2px 4px;
			position: relative;
			img {
				max-width: 100%;
			}
			&:hover {
				background: rgba(255, 255, 255, .1);
				&:before {
					position: absolute;
					left: 0;
					right: 0;
					top: 0;
					bottom: 0;
					text-align: center;
					background: rgba(255, 255, 255, .2);
					backdrop-filter: blur(3px);
					display: flex;
					align-items: center;
					justify-content: center;
					padding-left: 10px;
					padding-right: 10px;
					color: white;
				}
			}
			&.gift, &.product, &.subscription, &.tower {
				background: $gold;
				color: $gold-text;
				margin: 4px;
				border-radius: 4px;
				padding: 4px;
				&:hover {
					background: darken($gold, 5%);
				}
			}
			&.highlighted {
				background: $vaporwave-white;
				color: $dark;
				&:hover { background: darken($vaporwave-white, 5%); }
			}
			> .avatar, > .service {
				width: 24px;
				height: 24px;
				border-radius: 12px;
				object-fit: cover;
				overflow: hidden;
			}
			> .name:after { content: ' ' }
			&.chat > .name:after { content: ': ' }
			&.chat.gift > .name:after { content: ' ' }
			&.chat.product > .name:after { content: ' ' }
			> .content {
				display: inline;
				> img {
					max-width: 80px;
					max-height: 80px;

					&.emote {
						max-width: 32px;
						max-height: 32px;
					}
				}
			}
			&.self {
				color: fade-out($vaporwave-white, .4);
				&.highlighted {
					color: $dark;
				}
			}
			&.owner {
				color: $vaporwave-green;
				&.highlighted {
					color: $dark;
				}
			}
			> div.menubtn {
				display: none;
				position: absolute;
				font-size: 15px;
				right: 0;
				top: 0;
				bottom: 0;
				background: transparent;
				line-height: 100%;
				align-items: center;
				padding: 4px 24px;
				text-align: center;
				color: white;
				&:after {
					content: '⊕ Show';
				}
				backdrop-filter: blur(3px);
			}
			&.highlighted {
				> div.menubtn {
					&:hover {
					}
					&:after {
						content: '⊖ Hide';
					}
					color: $dark;
				}
			}
			&:hover > div.menubtn {
				display: inline-flex;
			}
			&:hover > div.menubtn:hover {
				background: rgba(255, 255, 255, .1);
			}
		}
	}

	> .chatLogScrollToBottom {
		position: absolute;
		bottom: 3.7em;
		right: 0;
		left: 0;
		text-align: center;
		pointer-events: none;

		> button {
			pointer-events: auto;
		}
	}

	.media {
		font-size: .7em;

		&:before {
			content: '▶︎ ';
		}
	}


	/* Entering Animation */
	.wedge-enter-active {
		transition: opacity 0.5s ease;
	}
	.wedge-enter, .wedge-leave-to {
		opacity: 0;
	}
	.wedge-enter-to {
		opacity: 1;
	}

	/* Leaving Animation */
	.wedge-leave-active {
		position: absolute;
		transition: opacity 0.5s ease;
	}
}
</style>
